import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAcademySlug } from '../../../../hooks/useAcademySlug';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { LEGAL_VERSIONS } from '@court-sync/shared';
import { AuthService } from '../../../../services/auth';
import { buildEmailVerificationPageUrl } from '../../../../utils/emailVerificationRedirect';
import { useAuthStore } from '../../../../store/auth';
import { usePublicBookingStore } from '../../../../store/publicBooking';
import { needsPublicBookingSessionPicker } from '../publicBookingSessionMode';
import { userFormSchema } from '../schemas';
import type { UserFormData } from '../schemas';
import { PublicBookingUserFormStep } from '../PublicBookingUserFormStep';
import { useOpenAcademyBookingLogin } from '@/hooks/useOpenAcademyBookingLogin';
import {
  appendPublicBookingWizardContextQueries,
  publicBookingBasePath,
  publicBookingStepPath,
  resolveManualInviteTokenForNavigation,
} from '../bookingPaths';
import { publicBookingService } from '../../../../services/publicBooking';

export function BookingRegisterStep() {
  const academySlug = useAcademySlug();
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const openBookingLogin = useOpenAcademyBookingLogin();
  const [searchParams] = useSearchParams();
  const { setUser: setAuthStoreUser } = useAuthStore();
  const academy = usePublicBookingStore((s) => s.academy);
  const lesson = usePublicBookingStore((s) => s.lesson);
  const userType = usePublicBookingStore((s) => s.userType);
  const selectedSessionDates = usePublicBookingStore((s) => s.selectedSessionDates);
  const campBookingMode = usePublicBookingStore((s) => s.campBookingMode);
  const groupBookingMode = usePublicBookingStore((s) => s.groupBookingMode);
  const invitationToken = usePublicBookingStore((s) => s.invitationToken);
  const manualInviteToken = usePublicBookingStore((s) => s.manualInviteToken);
  const waitlistClaimEntryId = usePublicBookingStore((s) => s.waitlistClaimEntryId);
  const setUserFormData = usePublicBookingStore((s) => s.setUserFormData);

  const needsCampSessionPicker = needsPublicBookingSessionPicker(
    lesson,
    campBookingMode,
    groupBookingMode
  );

  const userForm = useForm<UserFormData>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      phone: '',
      termsAccepted: false,
      privacyAccepted: false,
      marketingEmailConsent: false,
    },
  });

  const { setValue: setRegisterEmailField, getValues: getRegisterEmailFields } = userForm;

  React.useEffect(() => {
    const token = resolveManualInviteTokenForNavigation(searchParams, manualInviteToken);
    if (!token) {
      return undefined;
    }
    let cancelled = false;
    void publicBookingService.getManualInvitePreview(token).then((res) => {
      if (cancelled || !res.success || res.data?.inviteEmail == null || res.data.inviteEmail === '') {
        return;
      }
      const cur = getRegisterEmailFields('email');
      if (cur && cur.trim() !== '') return;
      setRegisterEmailField('email', res.data.inviteEmail);
    });
    return () => {
      cancelled = true;
    };
  }, [searchParams, manualInviteToken, setRegisterEmailField, getRegisterEmailFields]);

  const [submitting, setSubmitting] = React.useState(false);

  /** Invitation-only: `/signup` is reachable before `userType` is set (user must register first). */
  const allowRegisterWithoutUserTypeChoice =
    lesson?.isInvitationOnly === true && userType == null;

  React.useEffect(() => {
    if (!academySlug || !lessonId) return;
    if (userType === 'new' || allowRegisterWithoutUserTypeChoice) {
      return;
    }
    navigate(
      appendPublicBookingWizardContextQueries(
        publicBookingStepPath(academySlug, lessonId, 'register'),
        { invitationToken, manualInviteToken }
      ),
      { replace: true }
    );
  }, [
    userType,
    academySlug,
    lessonId,
    navigate,
    allowRegisterWithoutUserTypeChoice,
    invitationToken,
    manualInviteToken,
  ]);

  const handleSubmit = async (data: UserFormData) => {
    if (!academySlug || !lessonId) return;
    try {
      setSubmitting(true);
      setUserFormData(data);

      const returnUrl = appendPublicBookingWizardContextQueries(
        publicBookingBasePath(academySlug, lessonId),
        { invitationToken, manualInviteToken }
      );
      const { user, verificationEmailFailed } = await AuthService.signUpAsGuardian(
        data.email,
        data.password,
        data.firstName,
        data.lastName,
        returnUrl,
        {
          termsAccepted: data.termsAccepted,
          privacyAccepted: data.privacyAccepted,
          termsVersion: LEGAL_VERSIONS.TERMS,
          privacyVersion: LEGAL_VERSIONS.PRIVACY,
          marketingEmailConsent: data.marketingEmailConsent || false,
        },
        academy?.id
      );

      setAuthStoreUser(user);

      if (verificationEmailFailed) {
        navigate(buildEmailVerificationPageUrl({ email: data.email, returnUrl, academySlug }), {
          state: { verificationEmailSendFailed: true },
        });
        return;
      }

      toast.success('Account created! Please fill in the student details to continue.');
      navigate(
        appendPublicBookingWizardContextQueries(
          publicBookingStepPath(academySlug, lessonId, 'details'),
          { invitationToken, manualInviteToken }
        )
      );
    } catch (authError: unknown) {
      const err = authError as { code?: string };
      if (err.code === 'auth/email-already-in-use') {
        toast.error('An account with this email already exists. Please sign in instead.');
        openBookingLogin(
          academySlug,
          lessonId,
          {
            invitationToken,
            manualInviteToken,
            waitlistEntryId: waitlistClaimEntryId,
          },
          {
            prefillEmail: data.email,
            message: 'An account with this email already exists. Please sign in to continue your booking.',
          }
        );
        return;
      }
      throw authError;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicBookingUserFormStep
      form={userForm}
      onSubmit={handleSubmit}
      isSubmitting={submitting}
      onBack={() => {
        if (!academySlug || !lessonId) return;
        if (needsCampSessionPicker && selectedSessionDates.length > 0) {
          navigate(
            appendPublicBookingWizardContextQueries(
              publicBookingStepPath(academySlug, lessonId, 'sessions'),
              { invitationToken, manualInviteToken }
            )
          );
        } else {
          navigate(
            appendPublicBookingWizardContextQueries(
              publicBookingStepPath(academySlug, lessonId, 'register'),
              { invitationToken, manualInviteToken }
            )
          );
        }
      }}
    />
  );
}
